'use strict';

var babelHelpers = {};

babelHelpers.extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

babelHelpers;

/**
 * @typedef {object} QueueEvent
 *
 * @property {string} event
 * @property {*} data
 */

/**
 * @typedef {function} QueueListener
 *
 * @param {QueueEvent} event
 */

/**
 * @typedef {object} QueueOptions
 *
 * @property {number} maxSize
 * @property {number} interval in milliseconds
 *
 */

/**
 * @private
 *
 * @param {string} prop
 * @param {*} value
 * @returns {Function}
 */
function has(prop, value) {
    return function () {
        var obj = arguments.length <= 0 || arguments[0] === undefined ? {} : arguments[0];

        return obj[prop] === value;
    };
}

/**
 *
 * @class BufferedQueue
 *
 * @param {QueueOptions}
 *
 */
function BufferedQueue() {
    var _ref = arguments.length <= 0 || arguments[0] === undefined ? {} : arguments[0];

    var _ref$maxSize = _ref.maxSize;
    var maxSize = _ref$maxSize === undefined ? 100 : _ref$maxSize;
    var _ref$interval = _ref.interval;
    var interval = _ref$interval === undefined ? 60 * 1000 : _ref$interval;

    var items = [];

    var listeners = [];

    var noop = Function.prototype;

    var intervalId = 0;

    function emit(event, data) {
        var emissions = listeners.filter(has('event', event)).map(function (_ref2) {
            var listener = _ref2.listener;

            return new Promise(function (resolve) {
                return resolve(listener({ data: data, event: event }));
            }).then(noop, noop);
        });

        return Promise.all(emissions);
    }

    function flushIfFull() {
        if (items.length >= maxSize) {
            return flush();
        }

        return Promise.resolve();
    }

    function flushIfNotEmpty() {
        if (items.length > 0) {
            return flush();
        }

        return Promise.resolve();
    }

    /**
     * @memberOf BufferedQueue#
     *
     * @param {...*} args
     *
     * @return {Promise}
     */
    function add() {
        for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
            args[_key] = arguments[_key];
        }

        items.push.apply(items, args);

        return emit('add', args).then(flushIfFull);
    }

    /**
     * Empties the queue without invoking registered handlers
     *
     * @memberOf BufferedQueue#
     *
     * @return {Array<*>}
     */
    function clear() {
        return items.splice(0, items.length);
    }

    /**
     * Flushes the queue into the registered flush handlers
     *
     * @memberOf BufferedQueue#
     *
     * @return {Promise}
     */
    function flush() {
        return emit('flush', clear());
    }

    /**
     * @memberOf BufferedQueue#
     *
     * @param {QueueListener} [listener = null]
     * @param {string} [event='flush']
     */
    function off(listener) {
        var event = arguments.length <= 1 || arguments[1] === undefined ? 'flush' : arguments[1];

        listeners = listeners.filter(function (registered) {
            return (!listener || registered.listener === listener) && !(event && event === registered.event);
        });
    }

    /**
     * @memberOf BufferedQueue#
     *
     * @param {string} event
     * @param {QueueListener} listener
     */
    function on(event, listener) {
        listeners.push({ event: event, listener: listener });
    }

    /**
     * @memberOf BufferedQueue#
     *
     * @returns {number} The number of items that are currently queued up
     */
    function size() {
        return items.length;
    }

    /**
     * Enables automatic periodic flushing
     *
     * @memberOf BufferedQueue#
     *
     */
    function startInterval() {
        if (!intervalId) {
            intervalId = setInterval(flushIfNotEmpty, interval);
        }
    }

    /**
     * Disables automatic periodic flushing
     *
     * @memberOf BufferedQueue#
     */
    function stopInterval() {
        if (intervalId) {
            clearInterval(intervalId);

            intervalId = 0;
        }
    }

    startInterval();

    babelHelpers.extends(this, {
        add: add,
        clear: clear,
        flush: flush,
        on: on,
        off: off,
        size: size,
        startInterval: startInterval,
        stopInterval: stopInterval
    });
}

module.exports = BufferedQueue;